import { parsePayload } from '@hooks/useSubscription';
import { GridApiPro } from '@mui/x-data-grid-pro';
import { DeviceDataTableRow } from '@src/components/_shared/DeviceDataTable';
import { store } from '@src/store';
import {
  DataPoint,
  DataPointWithValue,
  isDataPointWithValue,
} from '@src/types/DataPoint';
import { getFieldNameFromDataPoint } from '@src/utils/gridUtils';
import {
  extractControllerIdFromTopic,
  extractEngineeringIdFromTopic,
} from '@utils/generateTopic';
import { MqttClient } from 'mqtt';
import { MutableRefObject, useEffect } from 'react';

function updateDataPointsWithValues(
  topic: DataPoint['fullTopic'],
  apiRef: MutableRefObject<GridApiPro>,
  value: unknown,
): void {
  const engineeringId = extractEngineeringIdFromTopic(topic);
  const controllerId = extractControllerIdFromTopic(topic);
  if (!engineeringId || !controllerId) {
    return;
  }

  let row = apiRef.current.getRow(engineeringId);
  let rowId = engineeringId;
  if (!row) {
    row = apiRef.current.getRow(controllerId);
    rowId = controllerId;
    if (!row) {
      return;
    }
  }

  const prevColumnValueObj = Object.values(row).find<DataPointWithValue>(
    (value: unknown): value is DataPointWithValue =>
      isDataPointWithValue(value) && value.fullTopic === topic,
  );

  if (!prevColumnValueObj) {
    return;
  }

  const alias = getFieldNameFromDataPoint(prevColumnValueObj);

  return apiRef.current?.updateRows([
    {
      id: rowId,
      [alias as string]: {
        ...prevColumnValueObj,
        value,
      },
    },
  ]);
}

function messageHandler(
  topic: string,
  message: Buffer,
  apiRef: MutableRefObject<GridApiPro>,
) {
  const { value } = parsePayload(message, topic);
  updateDataPointsWithValues(topic, apiRef, value);
}

function useGridSubscription(
  rows: DeviceDataTableRow[],
  apiRef: MutableRefObject<GridApiPro>,
  useNewClient?: MqttClient,
) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: safe
  useEffect(() => {
    if (rows.length === 0) {
      return () => {};
    }

    const state = store.getState();
    const mqttClient =
      useNewClient !== undefined ? useNewClient : state.mqtt.clientMQTT;

    const topics = rows.reduce<string[]>((acc, curr) => {
      const rowTopics: string[] = [];
      Object.values(curr).forEach((v) =>
        typeof v === 'object' && v !== null && 'fullTopic' in v
          ? rowTopics.push(v.fullTopic)
          : null,
      );

      return acc.concat(rowTopics);
    }, []);

    const onMessageCb = (topic: string, buffer: Buffer) => {
      if (topics.includes(topic)) {
        messageHandler(topic, buffer, apiRef);
      }
    };
    if (mqttClient) {
      mqttClient.on('message', onMessageCb);
      mqttClient.subscribe(topics);
    }
    return () => {
      if (mqttClient && mqttClient.connected && !mqttClient.disconnecting) {
        mqttClient.unsubscribe(topics);
        mqttClient.off('message', onMessageCb);
      }
    };
  }, [rows, useNewClient]);
}

export default useGridSubscription;
